import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import TropicalBackground from '../components/TropicalBackground';

/**
 * AuthPage - Page d'authentification Google via Emergent Auth
 * 
 * Version: 5.0.0 - Emergent Auth
 * 
 * PROTECTIONS AJOUTÉES:
 * - Anti-spam: Empêche les clics répétés
 * - État de chargement: Feedback visuel pendant la redirection
 * - Debounce: 2 secondes entre les tentatives
 * 
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS
 */

const AuthPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [lastClickTime, setLastClickTime] = useState(0);

  const handleGoogleLogin = useCallback(() => {
    // Protection anti-spam: minimum 2 secondes entre les clics
    const now = Date.now();
    if (now - lastClickTime < 2000) {
      console.log('[AUTH] Clic ignoré (anti-spam)');
      return;
    }
    
    // Éviter les doubles clics si déjà en cours
    if (isLoading) {
      console.log('[AUTH] Déjà en cours de redirection');
      return;
    }

    setLastClickTime(now);
    setIsLoading(true);
    
    console.log('[AUTH] Démarrage connexion Google via Emergent Auth');
    
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS
    // Utiliser window.location.origin pour obtenir le domaine actuel dynamiquement
    const redirectUrl = window.location.origin + '/dashboard';
    console.log('[AUTH] Redirect URL:', redirectUrl);
    
    // Rediriger vers Emergent Auth
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, [isLoading, lastClickTime]);

  return (
    <TropicalBackground className="min-h-screen flex flex-col items-center justify-center p-4">
      {/* Logo et titre */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center mb-8"
      >
        <div className="flex justify-center mb-4">
          <img 
            src="/logo.png" 
            alt="Leu Budget du Génie" 
            className={`h-20 w-20 rounded-2xl shadow-lg ${isLoading ? 'animate-pulse' : ''}`}
          />
        </div>
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          Leu Budget du Génie
        </h1>
        <p className="text-gray-600 text-sm">
          Gérez votre budget en toute simplicité
        </p>
      </motion.div>

      {/* Carte de connexion */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="w-full max-w-sm bg-white rounded-2xl shadow-xl p-8"
      >
        <h2 className="text-xl font-semibold text-center text-gray-800 mb-6">
          Connexion
        </h2>

        {/* Bouton Google */}
        <button
          onClick={handleGoogleLogin}
          disabled={isLoading}
          data-testid="google-login-btn"
          className={`w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-300 rounded-xl transition-all duration-200 shadow-sm ${
            isLoading 
              ? 'bg-gray-100 cursor-not-allowed opacity-70' 
              : 'hover:bg-gray-50 cursor-pointer'
          }`}
        >
          {isLoading ? (
            <>
              {/* Spinner de chargement */}
              <svg className="animate-spin h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="text-gray-500 font-medium">
                Connexion en cours...
              </span>
            </>
          ) : (
            <>
              {/* Google Icon SVG */}
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              <span className="text-gray-700 font-medium">
                Continuer avec Google
              </span>
            </>
          )}
        </button>

        {/* Texte explicatif */}
        <p className="mt-6 text-center text-xs text-gray-500">
          En continuant, vous acceptez nos conditions d'utilisation et notre politique de confidentialité.
        </p>
      </motion.div>

      {/* Footer */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        className="mt-8 text-center text-xs text-gray-400"
      >
        <p>Version 5.0.0</p>
      </motion.div>
    </TropicalBackground>
  );
};

export default AuthPage;
